import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Ban,
  Edit3,
  ExternalLink,
  Eye,
  MoreHorizontal,
  RefreshCw,
  ScanLine,
  Sparkles,
  Tag,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import StatusBadge from '../../components/StatusBadge';
import { FLAG_META } from '../../components/SignalStrip';
import { MenuItem } from './ContractDetailParts';
import { counterpartyPathForName } from '../counterparties/counterpartyApi';

export default function ContractDetailHeader({
  additionalParties,
  canWriteContracts,
  contract,
  contractId,
  flags,
  isAdmin,
  onOpenDeleteConfirm,
  onOpenScanModal,
  onOpenStatusModal,
  onOpenTerminateConfirm,
  onPublishToDrive,
  publishing,
  onViewAgreement,
}) {
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  useEffect(() => {
    if (!showMoreMenu) return undefined;
    const handler = (event) => {
      if (!event.target.closest('[data-more-menu]')) setShowMoreMenu(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showMoreMenu]);

  const openDriveFolder = () => {
    setShowMoreMenu(false);
    window.open(contract.driveContractFolderLink, '_blank', 'noopener');
  };

  return (
    <div className="page-header" style={{ alignItems: 'flex-start', marginBottom: 22 }}>
      <div style={{ minWidth: 0, flex: '1 1 320px' }}>
        <div className="row" style={{ gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <StatusBadge status={contract.status} />
          {contract.restricted && (
            <span className="chip red" title="Internal only — hidden from the User role">
              <span className="dot" /> Restricted
            </span>
          )}
          {flags.map((flag) => {
            const meta = FLAG_META[flag];
            if (!meta) return null;
            return (
              <span key={flag} className={`chip ${meta.tone}`}>
                <span className="dot" />
                {meta.label}
              </span>
            );
          })}
          {contract.screeningSessionId && (
            <Link
              to={`/intake/sessions/${contract.screeningSessionId}`}
              className="chip purple"
              style={{ textDecoration: 'none' }}
              title="Open the intake that produced this draft"
            >
              <Sparkles className="w-[10px] h-[10px]" /> From intake
            </Link>
          )}
          {Array.isArray(contract.tags) && contract.tags.map((tag) => (
            <Link
              key={tag}
              to={`/contracts?tags=${encodeURIComponent(tag)}`}
              className="chip gray"
              style={{ textDecoration: 'none' }}
              title={`See every contract tagged "${tag}"`}
            >
              <Tag className="w-[10px] h-[10px]" /> {tag}
            </Link>
          ))}
        </div>
        <h1 className="page-title" style={{ marginBottom: 6 }}>{contract.title}</h1>
        <div className="muted row" style={{ gap: 10, fontSize: 13, flexWrap: 'wrap' }}>
          {contract.shortName && (
            <>
              <span className="mono" style={{ color: 'var(--text-secondary)' }}>{contract.shortName}</span>
              <span style={{ opacity: 0.3 }}>·</span>
            </>
          )}
          {contract.counterparty && (
            /* Straight to the counterparty 360 page (doc-174) — the whole
               relationship, not just this one agreement. */
            <Link
              to={counterpartyPathForName(contract.counterparty)}
              title={`Everything on ${contract.counterparty}`}
            >
              {contract.counterparty}
            </Link>
          )}
          {additionalParties.length > 0 && (
            <>
              <span style={{ opacity: 0.3 }}>·</span>
              <span>{additionalParties.length} other part{additionalParties.length === 1 ? 'y' : 'ies'}</span>
            </>
          )}
          {contract.lightsparkEntity && (
            <>
              <span style={{ opacity: 0.3 }}>·</span>
              <span>{contract.lightsparkEntity}</span>
            </>
          )}
        </div>
      </div>

      <div className="row" style={{ gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
        {onViewAgreement && (
          <button onClick={onViewAgreement} className="btn outline">
            <Eye className="w-3.5 h-3.5" /> View agreement
          </button>
        )}
        {canWriteContracts && (
          <button onClick={onOpenStatusModal} className="btn outline">
            <RefreshCw className="w-3.5 h-3.5" /> Change status
          </button>
        )}
        {canWriteContracts && (
          <Link to={`/contracts/${contractId}/edit`} className="btn primary">
            <Edit3 className="w-3.5 h-3.5" /> Edit
          </Link>
        )}
        {canWriteContracts && (
          <div data-more-menu style={{ position: 'relative' }}>
            <button
              onClick={() => setShowMoreMenu((visible) => !visible)}
              className="icon-btn sm"
              title="More actions"
              aria-label="More actions"
              aria-expanded={showMoreMenu}
              aria-haspopup="menu"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {showMoreMenu && (
              <div
                className="card"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  minWidth: 200,
                  zIndex: 20,
                  padding: 6,
                  boxShadow: '0 12px 40px -12px rgba(0,0,0,0.25)',
                }}
              >
                <MenuItem
                  icon={ScanLine}
                  label="Scan document"
                  onClick={() => {
                    setShowMoreMenu(false);
                    onOpenScanModal();
                  }}
                />
                <MenuItem
                  icon={UploadCloud}
                  label={publishing ? 'Saving to Drive…' : 'Save to Drive'}
                  onClick={() => {
                    setShowMoreMenu(false);
                    onPublishToDrive();
                  }}
                />
                {contract.driveContractFolderLink && (
                  <MenuItem
                    icon={ExternalLink}
                    label="Open Drive folder"
                    onClick={openDriveFolder}
                  />
                )}
                {contract.status !== 'terminated' && (
                  <MenuItem
                    icon={Ban}
                    label="Terminate"
                    tone="yellow"
                    onClick={() => {
                      setShowMoreMenu(false);
                      onOpenTerminateConfirm();
                    }}
                  />
                )}
                {isAdmin && (
                  <MenuItem
                    icon={Trash2}
                    label="Delete permanently"
                    tone="red"
                    onClick={() => {
                      setShowMoreMenu(false);
                      onOpenDeleteConfirm();
                    }}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
